import SubmissionError from '../error/submissionError';
import {API_ENTRYPOINT} from '../config/entrypoint';
import router from '@/router';
import {translateError} from './errorMessages';
import i18n from '@/i18n';

// Výchozí typ pro API Platform (pokud ho používáš)
const MIME_TYPE = 'application/ld+json';

export default function (id, options = { explicitToken: null }) {

    if (typeof options.headers === 'undefined') {
        // Pokud options.headers neexistuje, vytvoříme nový Headers objekt
        options.headers = new Headers();
    } else if (!(options.headers instanceof Headers)) {
        // Pokud options.headers není instance Headers (je to obyčejný objekt),
        // převedeme ho na skutečný Headers objekt
        const oldHeaders = options.headers;
        options.headers = new Headers();
        for (const key in oldHeaders) {
            if (Object.prototype.hasOwnProperty.call(oldHeaders, key)) {
                options.headers.append(key, oldHeaders[key]);
            }
        }
    }

    if (options.headers.get('Accept') === null) {
        options.headers.set('Accept', MIME_TYPE);
    }
    if (options.body !== undefined && !(options.body instanceof FormData)
        && options.headers.get('Content-Type') === null) {
        // Pro login posíláme application/json, zde můžeme nechat ld+json jako výchozí
        if (options.method == 'PATCH') {
            options.headers.set('Content-Type', 'application/merge-patch+json');
        } else {
            // options.headers.set('Content-Type', 'application/merge-patch+json');
            options.headers.set('Content-Type', MIME_TYPE);
        }
    }

    // Logika pro token (ta byla správně)
    let tokenToUse = null;
    tokenToUse = sessionStorage.getItem('token'); // ⬅️ Čte token ze sessionStorage

    if (tokenToUse && options.headers.get('Authorization') === null) {
        options.headers.set('Authorization', `Bearer ${tokenToUse}`); // ⬅️ Vloží token
    }

    // Sestavení URL (bylo správně)
    // API_ENTRYPOINT je bare origin (bez /api) - ostatní volání v appce si /api připojují
    // samy přes new URL("api/...", API_ENTRYPOINT), tady je potřeba to udělat stejně
    // pro holé (nekonstruované) id jako 'login-user'/'accounts/5'.
    const entryPoint = API_ENTRYPOINT + (API_ENTRYPOINT.endsWith('/') ? '' : '/') + 'api/';
    const url = new URL(id, entryPoint);

    // Cross-origin cookies (dev: frontend :81 vs. API :444 jsou různé origin) -
    // bez credentials: 'include' by prohlížeč Set-Cookie z odpovědi (např.
    // mercureAuthorization z /api/mercure-token) vůbec neuložil. Bezpečné
    // nastavit vždy: prohlížeč cookie stejně pošle jen tam, kde nějaká pro
    // daný origin/path existuje, takže endpointy bez cookie nejsou ovlivněny.
    options.credentials = 'include';

    // Volání fetch a zpracování odpovědi (bylo správně)
    return fetch(url, options).then((response) => {
        if (options.dispatch === undefined) {
            console.error('Fetch utilita: Prosím předej Vuex dispatch metodu v options.dispatch');
        }

        // Zpracování 401 - špatný/expirovaný token NEBO špatné přihlašovací
        // údaje (login-user vrací 401 taky). Sjednoceno 2026-08-03: dřív se
        // vždycky zahodila skutečná backend hláška a vyhodila se genericka
        // "Unauthorized" - teď se tělo odpovědi doparsuje, přeloží přes
        // translateError() a rovnou přesměruje na Login (kde je hláška vidět
        // hned, ne až po dalším ručním kroku uživatele).
        if (response.status === 401 && options.dispatch) {
            return response.json().catch(() => ({})).then((json) => {
                const rawMessage = json['hydra:description'] || json['detail'] || json['message'] || response.statusText;
                const friendlyMessage = translateError(rawMessage);

                options.dispatch('user/logout', null, { root: true });
                options.dispatch('user/setError', friendlyMessage, { root: true });

                if (router.currentRoute.name !== 'Login') {
                    router.push({ name: 'Login' });
                }

                throw new Error(friendlyMessage);
            });
        }

        if (response.ok) return response;

        // Zpracování ostatních chyb. Pozor: musí to být dvouargumentové
        // .then(onFulfilled, onRejected), NE .then(cb).catch(cb2) - throw
        // uvnitř onFulfilled by jinak spadl do navazujícího .catch() a
        // schoval se za generickou "HTTP Error X" hlášku (nalezeno 2026-08-03
        // při řešení sjednocení chybových hlášek - tohle byl pravděpodobně
        // hlavní důvod, proč se v UI objevovaly jen generické/syrové texty).
        return response.json().then(
            (json) => {
                // 'detail' - RFC7807 problem+json format (dev debug=true error rendering
                // pro nezachycene Exception, viz UseItemAction.php) - bez tohohle fallbacku
                // se hlaska ztratila (statusText je pres HTTP/2 prazdny retezec).
                const error = json['hydra:description'] || json['detail'] || json['message'] || response.statusText;
                const friendlyError = translateError(error);
                if (!json.violations) throw Error(friendlyError);
                const errors = {_error: friendlyError};
                json.violations.forEach((violation) => errors[violation.propertyPath] = violation.message);
                throw new SubmissionError(errors);
            },
            () => {
                // Sem se dostane jen skutečné selhání response.json() (prázdné
                // tělo / ne-JSON odpověď), ne throwy z větve výše.
                throw new Error(i18n.t('common.apiErrors.httpErrorFallback', {status: response.status}));
            }
        );
    });
}